import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { useData } from '../contexts/DataContext';
import { formatZAR } from '../utils/currency';
import { Sparkles, Heart, Search, PenTool, CheckCircle2, Gift } from 'lucide-react';
export function GiftBoxesPage() {
  const {
    products
  } = useData();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  // Filter for gift box products only
  const giftBoxProducts = products.filter(product => product.category.toLowerCase().includes('gift box'));
  // Apply search filter
  const filteredProducts = giftBoxProducts.filter(product => product.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleCustomize = (productId: string) => {
    navigate('/occasions');
  };
  const showcaseImages = ['https://cdn.magicpatterns.com/uploads/2ki9REL6umPjZ2h8t8yg74/d0bfea9c2bbbd6813de05d288e551f9a.jpg', 'https://cdn.magicpatterns.com/uploads/ppznxwMMwqEdHyrHyF8ADm/bf213c5cb2223fe82b3966d9cbde55b4.jpg', 'https://cdn.magicpatterns.com/uploads/jdjHhKpfSwYvrjvSUnexC9/845814ae91c2550827f8bb15db100a74.jpg', 'https://cdn.magicpatterns.com/uploads/1BPuhgGpSt28P9WwmzTUv5/8b92aad5ee096dfedf5eaccd609dda91.jpg', 'https://cdn.magicpatterns.com/uploads/daD8PkBgNoHU9hXDTktK2P/c489c05001c51ab7b51d2a03818888c6.jpg'];
  return <div className="min-h-screen bg-white" data-id="element-2528">
      <Navbar data-id="element-2529" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-gradient-to-b from-rose-50/50 to-white" data-id="element-2530">
        {/* Decorative Background */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-2531">
          <motion.div animate={{
          y: [0, -20, 0],
          rotate: [0, 5, 0]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-20 left-[10%] w-64 h-64 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30" data-id="element-2532" />
          <motion.div animate={{
          y: [0, 30, 0],
          rotate: [0, -5, 0]
        }} transition={{
          duration: 7,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1
        }} className="absolute top-40 right-[10%] w-72 h-72 bg-rose-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30" data-id="element-2533" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2534">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-2535">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-rose-100 shadow-sm mb-6" data-id="element-2536">
              <Gift className="w-4 h-4 text-rose-400 mr-2" data-id="element-2537" />
              <span className="text-sm font-medium text-gray-600" data-id="element-2538">
                Thoughtfully Curated Collections
              </span>
            </div>

            <h1 className="font-serif text-5xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6" data-id="element-2539">
              Curated Gift Boxes <br data-id="element-2540" />
              <span className="text-rose-500" data-id="element-2541">Wrapped with Love</span>
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8" data-id="element-2542">
              Discover our collection of hand-picked treasures, beautifully
              packaged to create unforgettable unboxing moments for your loved
              ones.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2543">
              <Button size="lg" onClick={() => document.getElementById('products')?.scrollIntoView({
              behavior: 'smooth'
            })} rightIcon={<Heart className="w-5 h-5" data-id="element-2545" />} data-id="element-2544">
                Browse Collection
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate('/occasions')} data-id="element-2546">
                Custom Gift Boxes
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Personalized Showcase Section */}
      <section className="py-20 bg-white overflow-hidden" data-id="element-2547">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2548">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-2549">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-2550">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-rose-50 border border-rose-100 shadow-sm mb-6" data-id="element-2551">
                <Sparkles className="w-4 h-4 text-rose-500 mr-2" data-id="element-2552" />
                <span className="text-sm font-medium text-rose-900" data-id="element-2553">
                  Signature Selections
                </span>
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-2554">
                Thoughtfully Curated <br data-id="element-2555" />
                <span className="text-rose-500" data-id="element-2556">For Every Occasion</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-2557">
                From pampering spa sets to celebration boxes, each collection is
                designed to delight. Personalize with names, messages, and
                special touches.
              </p>

              <div className="flex flex-wrap items-center gap-6 mb-10" data-id="element-2558">
                <div className="bg-rose-50 px-6 py-4 rounded-2xl border border-rose-100 shadow-sm" data-id="element-2559">
                  <span className="block text-sm text-gray-500 font-medium uppercase tracking-wider mb-1" data-id="element-2560">
                    Starting from
                  </span>
                  <span className="block text-4xl font-bold text-rose-600" data-id="element-2561">
                    R400
                  </span>
                </div>
                <div className="h-16 w-px bg-gray-200 hidden sm:block" data-id="element-2562"></div>
                <div className="flex flex-col gap-2" data-id="element-2563">
                  <span className="flex items-center text-gray-700 font-medium" data-id="element-2564">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2" data-id="element-2565" />{' '}
                    Curated Collections
                  </span>
                  <span className="flex items-center text-gray-700 font-medium" data-id="element-2566">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2" data-id="element-2567" />{' '}
                    Personalized Touch
                  </span>
                  <span className="flex items-center text-gray-700 font-medium" data-id="element-2568">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2" data-id="element-2569" />{' '}
                    Beautiful Packaging
                  </span>
                </div>
              </div>

              <Button size="lg" onClick={() => navigate('/occasions')} className="w-full sm:w-auto shadow-lg shadow-rose-200" rightIcon={<PenTool className="w-5 h-5" data-id="element-2571" />} data-id="element-2570">
                Create Custom Gift Box
              </Button>
            </motion.div>

            <div className="grid grid-cols-2 gap-4 sm:gap-6" data-id="element-2572">
              <div className="space-y-4 sm:space-y-6" data-id="element-2573">
                {showcaseImages.slice(0, 2).map((img, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: idx * 0.15
              }} className="relative rounded-2xl overflow-hidden shadow-lg aspect-[4/5] group cursor-pointer" data-id="element-2574">
                    <img src={img} alt={`Gift Box ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-2575" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4" data-id="element-2576">
                      <span className="text-white font-medium text-sm flex items-center" data-id="element-2577">
                        <Heart className="w-4 h-4 mr-2 fill-current" data-id="element-2578" /> View
                        Details
                      </span>
                    </div>
                  </motion.div>)}
              </div>
              <div className="space-y-4 sm:space-y-6 mt-8 sm:mt-12" data-id="element-2579">
                {showcaseImages.slice(2, 4).map((img, idx) => <motion.div key={idx + 2} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: (idx + 2) * 0.15
              }} className="relative rounded-2xl overflow-hidden shadow-lg aspect-[4/5] group cursor-pointer" data-id="element-2580">
                    <img src={img} alt={`Gift Box ${idx + 3}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-2581" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4" data-id="element-2582">
                      <span className="text-white font-medium text-sm flex items-center" data-id="element-2583">
                        <Heart className="w-4 h-4 mr-2 fill-current" data-id="element-2584" /> View
                        Details
                      </span>
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-16 bg-gray-50" data-id="element-2585">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2586">
          <div className="text-center mb-12" data-id="element-2587">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-4" data-id="element-2588">
              Explore Our Collection
            </h2>
            <div className="max-w-md mx-auto relative" data-id="element-2589">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-2590" />
              <input type="text" placeholder="Search gift boxes..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-white border-2 border-rose-100 rounded-full focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all shadow-sm" data-id="element-2591" />
            </div>
          </div>

          {/* Product Count */}
          <div className="mb-8 flex justify-between items-center" data-id="element-2592">
            <p className="text-gray-600" data-id="element-2593">
              <span className="font-semibold text-gray-900" data-id="element-2594">
                {filteredProducts.length}
              </span>{' '}
              gift box{filteredProducts.length !== 1 ? 'es' : ''} available
            </p>
          </div>

          {/* Products Grid */}
          {filteredProducts.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-id="element-2595">
              {filteredProducts.map((product, index) => <motion.div key={product.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.05
          }} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-rose-50" data-id="element-2596">
                  <div className="relative aspect-square overflow-hidden bg-gray-100" data-id="element-2597">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110" data-id="element-2598" />
                    {product.isNew && <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-rose-500 shadow-sm" data-id="element-2599">
                        NEW
                      </div>}
                    <div className="absolute top-4 right-4 opacity-0 transform translate-x-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" data-id="element-2600">
                      <button className="p-2 bg-white rounded-full shadow-md text-gray-400 hover:text-rose-500 transition-colors" data-id="element-2601">
                        <Heart className="w-5 h-5" data-id="element-2602" />
                      </button>
                    </div>
                  </div>

                  <div className="p-5" data-id="element-2603">
                    <div className="text-xs font-medium text-rose-500 mb-1 uppercase tracking-wider" data-id="element-2604">
                      {product.category}
                    </div>
                    <h3 className="font-serif text-lg font-bold text-gray-900 mb-2 line-clamp-2 min-h-[3.5rem]" data-id="element-2605">
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between mb-4" data-id="element-2606">
                      <span className="text-xl font-bold text-gray-900" data-id="element-2607">
                        {formatZAR(product.price)}
                      </span>
                      {product.stock > 0 ? <span className="text-xs text-green-600 font-medium" data-id="element-2608">
                          In Stock
                        </span> : <span className="text-xs text-red-600 font-medium" data-id="element-2609">
                          Out of Stock
                        </span>}
                    </div>

                    <Button onClick={() => handleCustomize(product.id)} className="w-full" size="sm" leftIcon={<PenTool className="w-4 h-4" data-id="element-2611" />} data-id="element-2610">
                      Customize This
                    </Button>
                  </div>
                </motion.div>)}
            </div> : <div className="text-center py-16" data-id="element-2612">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2613">
                <Search className="w-8 h-8 text-rose-400" data-id="element-2614" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2615">
                No gift boxes found
              </h3>
              <p className="text-gray-600" data-id="element-2616">
                Try adjusting your search to find what you're looking for.
              </p>
            </div>}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white" data-id="element-2617">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2618">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2619">
            {[{
            icon: <Gift className="w-6 h-6" data-id="element-2620" />,
            title: 'Curated Collections',
            description: 'Hand-picked items that complement each other perfectly'
          }, {
            icon: <PenTool className="w-6 h-6" data-id="element-2621" />,
            title: 'Personalized Touch',
            description: 'Add names, messages, and custom details to make it unique'
          }, {
            icon: <Sparkles className="w-6 h-6" data-id="element-2622" />,
            title: 'Perfect for Any Occasion',
            description: 'Birthdays, anniversaries, or just because - we have it covered'
          }].map((feature, index) => <motion.div key={feature.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="text-center p-6" data-id="element-2623">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-rose-100 rounded-full text-rose-500 mb-4" data-id="element-2624">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2" data-id="element-2625">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-2626">{feature.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-white to-rose-50/30" data-id="element-2627">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2628">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-2629">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-100 rounded-full mb-6" data-id="element-2630">
              <Gift className="w-8 h-8 text-rose-500" data-id="element-2631" />
            </div>

            <h2 className="font-serif text-4xl font-bold text-gray-900 mb-4" data-id="element-2632">
              Gift with Intention
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-2633">
              Make every gift meaningful with our curated collections.
              Thoughtfully designed to create moments of joy and connection.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2634">
              <Button size="lg" onClick={() => document.getElementById('products')?.scrollIntoView({
              behavior: 'smooth'
            })} rightIcon={<Search className="w-5 h-5" data-id="element-2636" />} data-id="element-2635">
                Browse Gift Boxes
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate('/about')} data-id="element-2637">
                Learn More About Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-2638" />
    </div>;
}